/**
 * Safe-area insets for JavaScript positioners.
 *
 * index.html ships `viewport-fit=cover`, so on a notched phone the page runs
 * under the Dynamic Island / notch and the home indicator. CSS reads those
 * bands through the `--mf-safe-*` tokens in main.css, but JS that positions
 * elements by pixel (tutorial tips, coach-mark cues, popovers) needs numbers.
 *
 * `getComputedStyle(root).getPropertyValue('--mf-safe-left')` is no good: a
 * custom property computes to its token text, not to resolved pixels. So we
 * measure a throwaway probe whose padding IS the env() values — the only way
 * to get the browser to resolve them for us.
 *
 * The result is cached and invalidated on resize / orientation change, because
 * the insets genuinely swap sides when a phone rotates.
 */
export interface SafeAreaInsets { top: number; right: number; bottom: number; left: number }
export interface SafeAreaBox { left: number; top: number; right: number; bottom: number }
export interface SafeAreaApi {
  /** Current insets in CSS pixels. */
  insets(): SafeAreaInsets;
  /** The rectangle content may occupy, in client coordinates, minus `extra` on every side. */
  box(extra?: number): SafeAreaBox;
  /** Clamp a width x height box into the safe area. */
  clamp(left: number, top: number, width: number, height: number, extra?: number): { left: number; top: number };
  invalidate(): void;
}
// Assigned by the IIFE below before anything can import it; typed here so JS
// consumers (checkJs) see the API instead of `{}`.
let SafeArea = {} as SafeAreaApi;

(function () {
  const ZERO = { top: 0, right: 0, bottom: 0, left: 0 };
  let cached = null;

  function measure() {
    if (!document?.documentElement || typeof window.getComputedStyle !== 'function') return { ...ZERO };
    const probe = document.createElement('div');
    probe.id = 'moonfall-safe-area-probe';
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = [
      'position:fixed',
      'visibility:hidden',
      'pointer-events:none',
      'padding-top:env(safe-area-inset-top, 0px)',
      'padding-right:env(safe-area-inset-right, 0px)',
      'padding-bottom:env(safe-area-inset-bottom, 0px)',
      'padding-left:env(safe-area-inset-left, 0px)',
    ].join(';');
    document.documentElement.appendChild(probe);
    const style = window.getComputedStyle(probe);
    const px = (value) => {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const insets = {
      top: px(style.paddingTop),
      right: px(style.paddingRight),
      bottom: px(style.paddingBottom),
      left: px(style.paddingLeft),
    };
    probe.remove();
    return insets;
  }

  /** Current insets in CSS pixels. Cheap: measured once per viewport change. */
  function insets() {
    if (!cached) cached = measure();
    return { ...cached };
  }

  /**
   * The rectangle content is allowed to occupy, in client coordinates.
   * `extra` adds a uniform cosmetic margin on top of the physical inset.
   */
  function box(extra = 0) {
    const i = insets();
    return {
      left: i.left + extra,
      top: i.top + extra,
      right: window.innerWidth - i.right - extra,
      bottom: window.innerHeight - i.bottom - extra,
    };
  }

  /** Clamp a width x height box into the safe area (never off the near edge). */
  function clamp(left, top, width, height, extra = 0) {
    const safe = box(extra);
    return {
      left: Math.max(safe.left, Math.min(safe.right - width, left)),
      top: Math.max(safe.top, Math.min(safe.bottom - height, top)),
    };
  }

  const invalidate = () => { cached = null; };
  window.addEventListener('resize', invalidate, { passive: true });
  window.addEventListener('orientationchange', invalidate, { passive: true });

  SafeArea = { insets, box, clamp, invalidate };
})();

export { SafeArea };
